'use client'
import { Button, Checkbox, FormField, Input, LinkInline } from '@cleanlab/design-system/components'
import { useForm } from '@tanstack/react-form'
import parsePhoneNumber from 'libphonenumber-js'
import React, { useCallback, useState } from 'react'

import { useActivateAccount } from '@/api/users'
import { PhoneNumberInput } from '@/components/PhoneNumberInput/PhoneNumberInput'
import { requiredFieldValidator } from '@/global/utils/fieldValidators'
import { useUser } from '@/providers/account/UserContextProvider'

function ActivateAccount() {
  const { userData } = useUser()
  const { email = '' } = userData ?? {}

  const { mutateAsync: activateAccount } = useActivateAccount()
  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email,
      phone_number: '',
      user_provided_company_name: '',
      discovery_source: '',
      terms_accepted: false,
    },
    onSubmit: async ({ value }) => {
      try {
        const { first_name, last_name, ...otherFields } = value
        const nonEmptyFields = {
          first_name,
          last_name,
          ...Object.fromEntries(Object.entries(otherFields).filter(([_, v]) => v !== '')),
        }
        await activateAccount({ body: nonEmptyFields })
      } catch (error) {
        console.error('Failed to activate account:', error)
      }
    },
  })

  const [showErrors, setShowErrors] = useState(false)

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setShowErrors(true)
      form.handleSubmit()
    },
    [form]
  )

  return (
    <div className="flex flex-col gap-4">
      <h1 className="type-body-300 font-medium text-text-primary">Activate your free trial</h1>
      <form onSubmit={onSubmit} className="flex w-full flex-col gap-6" noValidate>
        <div className="flex gap-4">
          <form.Field
            name="first_name"
            validators={{
              onChange: requiredFieldValidator,
            }}
          >
            {(field) => (
              <FormField
                required
                label="First name"
                error={showErrors ? field.state.meta.errors.join(', ') : undefined}
                className="flex-1"
              >
                <Input
                  surfaceLevel="1"
                  placeholder="Enter your first name"
                  autoComplete="given-name"
                  onChange={(e) => field.handleChange(e.target.value)}
                  value={field.state.value}
                  required
                />
              </FormField>
            )}
          </form.Field>

          <form.Field
            name="last_name"
            validators={{
              onChange: requiredFieldValidator,
            }}
          >
            {(field) => (
              <FormField
                required
                label="Last name"
                error={showErrors ? field.state.meta.errors.join(', ') : undefined}
                className="flex-1"
              >
                <Input
                  surfaceLevel="1"
                  placeholder="Enter your last name"
                  autoComplete="family-name"
                  onChange={(e) => field.handleChange(e.target.value)}
                  value={field.state.value}
                  required
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <form.Field name="email">
          {(field) => (
            <FormField label="Email">
              <Input surfaceLevel="1" value={field.state.value} disabled />
            </FormField>
          )}
        </form.Field>
        <form.Field
          name="phone_number"
          validators={{
            onChange: ({ value }) => {
              if (!value?.trim()) {
                return null
              }
              const num = parsePhoneNumber(value)
              return !num?.isPossible() ? 'You must enter a valid phone number' : null
            },
          }}
        >
          {(field) => {
            return (
              <FormField
                label="Phone Number (optional)"
                error={showErrors ? field.state.meta.errors.join(', ') : undefined}
              >
                <PhoneNumberInput
                  value={field.state.value}
                  onChange={(val) => field.handleChange(val ?? '')}
                />
              </FormField>
            )
          }}
        </form.Field>
        <form.Field name="user_provided_company_name">
          {(field) => (
            <FormField label="Company name (optional)">
              <Input
                autoComplete="organization"
                surfaceLevel="1"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </FormField>
          )}
        </form.Field>

        <form.Field name="discovery_source">
          {(field) => (
            <FormField label="How did you hear about us? (optional)">
              <Input
                surfaceLevel="1"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </FormField>
          )}
        </form.Field>
        <form.Field
          name="terms_accepted"
          validators={{
            onChange: ({ value }) => (!value ? 'You must accept the terms of service' : null),
          }}
        >
          {(field) => (
            <FormField required error={showErrors ? field.state.meta.errors.join(', ') : undefined}>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={!!field.state.value}
                  onCheckedChange={(checked) => field.handleChange(checked === true)}
                />
                <span>
                  I agree to the{' '}
                  <LinkInline href="https://cleanlab.ai/terms/" target="_blank">
                    terms of service
                  </LinkInline>
                  .*
                </span>
              </label>
            </FormField>
          )}
        </form.Field>

        <Button type="submit" className="w-fit">
          Submit
        </Button>
      </form>
    </div>
  )
}

export default ActivateAccount
